import { Link } from 'react-router-dom';
import { Check, Sparkles, Zap, Building2, CreditCard, Apple, Smartphone, Gift } from 'lucide-react';
import { useCurrency } from '../hooks/useCurrency';
import { PLANS, formatPrice, CURRENCY_META } from '../lib/pricing';
import CurrencyPicker from '../components/CurrencyPicker';

export default function Pricing() {
  const { currency } = useCurrency();

  // Localised hero copy — "Start at ₹249" reads far more naturally for Indian
  // users than a dollar figure they'd have to mentally convert. The format
  // helper handles symbol placement and grouping per-locale.
  const tryPrice = formatPrice(PLANS[0].amounts[currency], currency);

  return (
    <div className="gradient-hero">
      <div className="container-wide py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-display font-bold">Simple, transparent pricing</h1>
          <p className="mt-3 text-ink-600 text-lg">
            Start at {tryPrice}. Upgrade when you need more. Cancel anytime.
          </p>

          <div className="mt-6 flex items-center justify-center flex-wrap gap-3 text-sm text-ink-600">
            <span className="flex items-center gap-1"><CreditCard className="w-4 h-4" /> Cards</span>
            <span className="flex items-center gap-1"><Apple className="w-4 h-4" /> Apple Pay</span>
            <span className="flex items-center gap-1"><Smartphone className="w-4 h-4" /> UPI / Google Pay</span>
            <span className="flex items-center gap-1"><Smartphone className="w-4 h-4" /> WeChat Pay</span>
            <span className="text-ink-400">· powered by Stripe</span>
          </div>
          <p className="mt-2 text-xs text-ink-500">
            Charges appear on your statement as <strong className="text-ink-700">PRIME AXIS</strong>, our payments partner.
          </p>

          <div className="mt-4">
            <CurrencyPicker />
            <p className="mt-2 text-xs text-ink-500">
              Showing prices in {CURRENCY_META[currency].label}. You can change this at checkout.
            </p>
          </div>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
          {PLANS.map((p) => (
            <div key={p.id}
              className={`card p-6 relative flex flex-col ${p.highlighted ? 'ring-2 ring-brand-500 shadow-pop lg:-translate-y-2' : ''}`}>
              {p.highlighted && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 chip bg-brand-600 text-white px-3 text-xs whitespace-nowrap">
                  <Sparkles className="w-3.5 h-3.5 mr-1" /> Most popular
                </span>
              )}
              {p.id === 'try' && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 chip bg-emerald-600 text-white px-3 text-xs whitespace-nowrap">
                  <Gift className="w-3.5 h-3.5 mr-1" /> Start here
                </span>
              )}
              <div className="flex items-center gap-2">
                {p.id === 'try'     && <Gift className="w-5 h-5 text-emerald-600" />}
                {p.id === 'starter' && <Zap className="w-5 h-5 text-amber-500" />}
                {p.id === 'pro'     && <Sparkles className="w-5 h-5 text-brand-600" />}
                {p.id === 'team'    && <Building2 className="w-5 h-5 text-fuchsia-600" />}
                <h3 className="text-lg font-semibold">{p.name}</h3>
              </div>
              {p.tagline && <p className="text-xs text-ink-500 mt-1">{p.tagline}</p>}
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-display font-bold">
                  {formatPrice(p.amounts[currency], currency)}
                </span>
                <span className="text-ink-500 text-sm">{p.cadence === 'one-time' ? 'one-time' : '/ month'}</span>
              </div>
              {p.credits && <p className="text-sm text-ink-500 mt-1">{p.credits} credits · never expire</p>}
              {p.monthlyCap && <p className="text-sm text-ink-500 mt-1">{p.monthlyCap} operations / month</p>}
              <ul className="mt-5 space-y-2 text-sm flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link to={`/checkout/${p.id}`}
                className={`mt-6 w-full text-center block ${p.highlighted ? 'btn-primary' : 'btn-secondary'}`}>
                {p.id === 'try' ? `Start for ${formatPrice(p.amounts[currency], currency)}` : `Get ${p.name}`}
              </Link>
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-xs text-ink-500 max-w-2xl mx-auto">
          Pro and Team plans are capped monthly to keep pricing sustainable. Hit the cap?
          Additional operations are billed at a per-op rate or upgrade to Team.
        </p>

        <div className="mt-16 max-w-3xl mx-auto card p-8">
          <h3 className="text-lg font-semibold">Frequently asked questions</h3>
          <div className="mt-4 divide-y divide-ink-100">
            {faqs.map((f) => (
              <details key={f.q} className="py-3 group">
                <summary className="cursor-pointer font-medium text-ink-800 flex items-center justify-between">
                  {f.q}
                  <span className="text-ink-400 group-open:rotate-180 transition-transform">▾</span>
                </summary>
                <p className="mt-2 text-sm text-ink-600">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const faqs = [
  { q: 'What does the Try plan include?', a: 'Five credits to use however you like — evaluate a report, generate one, or grade against a rubric. Credits never expire. It\'s the cheapest way to see the quality before committing.' },
  { q: 'What counts as one operation?', a: 'Each AI-backed run — evaluate a report, generate a report, grade by rubric — consumes one operation or credit. The "Improve to A-grade" rewrite on Pro counts as 3.' },
  { q: 'Which currency will I be charged in?', a: 'We auto-detect a currency that matches your region (INR in India, SGD in Singapore, USD elsewhere). You can switch it anytime with the currency selector on this page. Your choice is remembered for future visits.' },
  { q: 'What happens if I hit the Pro 200/month cap?', a: 'Your account still works — additional operations are billed at a small per-op rate. You can also upgrade to Team for 1,000 ops/month. The cap resets on your billing date.' },
  { q: 'Can I pay with UPI, Apple Pay, or WeChat Pay?', a: 'Yes. Checkout uses Stripe Payment Element which supports cards, Apple Pay, Google Pay, UPI (India), WeChat Pay, and Link. The options shown depend on your device and region.' },
  { q: 'Do credits expire?', a: 'Credit pack credits (Try, Starter) never expire. Pro and Team subscribers get a monthly allotment; unused operations do not roll over.' },
  { q: 'Is my report used to train AI?', a: 'No. Uploaded text is processed for your grading run and then discarded unless you opt in to save it.' },
  { q: 'Can I request a refund?', a: 'Yes — within 14 days for subscriptions, no questions asked. Email support@gradelab.club.' },
];
